import { BasicButton, Stack, Text } from "@certn/cascadia";
import { Divider, TextInputFormikField } from "@certn/cascadia-react";
import { FormikProps } from "formik";
import { Trans, useTranslation } from "react-i18next";

import { createEmptyQuestion, CustomQuestionnaireTemplateInput } from "./utils";
import { EmptyState } from "../../packages/components/EmptyState";
import isEmpty from "lodash/isEmpty";
import { CustomQuestionnaireTemplateQuestionField } from "./CustomQuestionnaireQuestionField";

export type CustomQuestionnaireTemplateFormProps = {
  type: "create" | "update";
  values: CustomQuestionnaireTemplateInput;
  setValues: FormikProps<CustomQuestionnaireTemplateInput>["setValues"];
  setFieldValue: FormikProps<CustomQuestionnaireTemplateInput>["setFieldValue"];
};

export function CustomQuestionnaireTemplateForm({
  type,
  values,
  setValues,
  setFieldValue,
}: CustomQuestionnaireTemplateFormProps) {
  const { t } = useTranslation();

  const orderedQuestions = [...values.questions].sort(
    (a, b) => (a.display_priority ?? 0) - (b.display_priority ?? 0)
  );

  const addQuestion = () => {
    setValues((prev) => {
      const display_priority = prev.questions.length + 1;

      return {
        ...prev,
        questions: [...prev.questions, createEmptyQuestion(display_priority)],
      };
    });
  };

  return (
    <Stack gap="space-6">
      <TextInputFormikField
        name="name"
        isRequired
        label={t("Template name")}
        testID="template-name"
        description={t("This will also be displayed to the applicant.")}
      />
      <Divider />
      <Stack gap="space-2">
        <Text style="global/heading-large">
          {isEmpty(values.questions)
            ? t("Questions")
            : t("Questions ({{number}})", { number: values.questions.length })}
        </Text>
        {isEmpty(values.questions) ? (
          <EmptyState
            message={
              <Text style="global/heading-medium">
                {t("Get started by adding a question")}
              </Text>
            }
            button={
              <BasicButton leftIcon="add" onPress={addQuestion}>
                {t("Add question")}
              </BasicButton>
            }
          />
        ) : (
          <Stack gap="space-6">
            {orderedQuestions.map((question, index) => (
              <CustomQuestionnaireTemplateQuestionField
                key={question.id}
                type={type}
                currentQuestion={question}
                index={index}
                setFieldValue={setFieldValue}
                setValues={setValues}
                values={values}
              />
            ))}
            <BasicButton
              onPress={addQuestion}
              testID="add-question"
              isDisabled={type === "update"}
            >
              {t("Add question")}
            </BasicButton>
          </Stack>
        )}
      </Stack>
      <Divider />
      <Stack gap="space-5">
        <Text style="input/helper" color="text-weak">
          <Trans>
            You are solely responsible for the content and legal compliance of
            any custom questions you create, including compliance with
            applicable employment, human rights, privacy, data protection, and
            anti-discrimination laws. Certn does not review or approve the
            legality, necessity, or appropriateness of your questions. You are
            encouraged to review all custom questions with legal counsel prior
            to use.
          </Trans>
        </Text>
        <Text style="input/helper" color="text-weak">
          <Trans>
            You should carefully consider whether it is lawful and necessary to
            request sensitive or special category personal data (such as health,
            disability, criminal history, racial or ethnic origin, religious
            beliefs, sexual orientation, or immigration status), as the
            collection of such information may be restricted or prohibited by
            law or require additional safeguards or consent.
          </Trans>
        </Text>
        <Text style="input/helper" color="text-weak">
          <Trans>
            You are responsible for the lawful collection and use of such data
            and agree to indemnify and hold harmless Certn from any claims,
            regulatory actions, penalties, or losses arising from your custom
            questions or the data collected through them.
          </Trans>
        </Text>
      </Stack>
    </Stack>
  );
}
